import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom'; 
import api from '../axiosConfig';
import errorHandler from '../utils/errorHandler';
import './knowledge_base.css';

const KnowledgeBaseHome = () => {
  const [categories, setCategories] = useState([]);
  const [popularArticles, setPopularArticles] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [categoriesRes, popularRes, recentRes, statsRes] = await Promise.all([
        api.get('/kb/categories'), 
        api.get('/kb/analytics/popular?limit=5'), 
        api.get('/kb/analytics/recent?limit=5'), 
        api.get('/kb/analytics/stats') // 
      ]);

      setCategories(categoriesRes.data.categories);
      setPopularArticles(popularRes.data.popular_articles);
      setRecentArticles(recentRes.data.recent_articles);
      setStats(statsRes.data.stats);
      setLoading(false);
    } catch (error) {
      console.error('Error loading KB data:', error);
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim().length < 2) return;

    setIsSearching(true);
    try {
      const response = await api.get('/kb/search', { 
        params: { q: searchQuery }
      });
      setSearchResults(response.data.results);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="knowledge-base">
      {/* Hero Section with Search */}
      <section className="kb-hero">
        <div className="container">
          <h1>SalonLogic Pro Knowledge Base</h1>
          <p className="lead">
            Everything you need to know about running a successful salon business
          </p>

          <form onSubmit={handleSearch} className="kb-search">
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <button type="submit" disabled={isSearching}>
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </form>

          {searchResults.length > 0 && (
            <div className="search-results">
              <h3>Search Results ({searchResults.length})</h3>
              <ul>
                {searchResults.map(article => (
                  <li key={article.id}>
                    <Link to={`/kb/articles/${article.slug}`}>
                      <h4>{article.title}</h4>
                      <p>{article.excerpt}</p>
                      <span className="meta">
                        {article.category_name} • {article.views} views
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Stats Bar */}
      {stats && (
        <section className="kb-stats">
          <div className="container">
            <div className="stat-item">
              <span className="stat-number">{stats.total_articles}</span>
              <span className="stat-label">Articles</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.total_categories}</span>
              <span className="stat-label">Categories</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.total_views.toLocaleString()}</span>
              <span className="stat-label">Views</span>
            </div>
          </div>
        </section>
      )}

      {/* Categories Grid */}
      <section className="kb-categories">
        <div className="container">
          <h2>Browse by Category</h2>
          <div className="category-grid">
            {categories.map(category => (
              <Link
                key={category.id}
                to={`/kb/categories/${category.slug}`}
                className="category-card"
                style={{ borderLeftColor: category.color }}
              >
                {category.icon && (
                  <i className={`fas fa-${category.icon} category-icon`}></i>
                )}
                <h3>{category.name}</h3>
                <p>{category.description || `${category.article_count} articles`}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Articles */}
      <section className="kb-popular">
        <div className="container">
          <h2>Popular Articles</h2>
          <div className="article-list">
            {popularArticles.map(article => (
              <Link
                key={article.id}
                to={`/kb/articles/${article.slug}`}
                className="article-card"
              >
                <h3>{article.title}</h3>
                <p>{article.excerpt}</p>
                <div className="article-meta">
                  <span className="category">{article.category_name}</span>
                  <span className="views">{article.views} views</span>
                  {article.helpfulness_ratio && (
                    <span className="helpful">
                      {Math.round(article.helpfulness_ratio * 100)}% helpful
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Articles */}
      <section className="kb-recent">
        <div className="container">
          <h2>Recently Updated</h2>
          <div className="article-list">
            {recentArticles.map(article => (
              <Link
                key={article.id}
                to={`/kb/articles/${article.slug}`}
                className="article-card"
              >
                <h3>{article.title}</h3>
                <p>{article.excerpt}</p>
                <div className="article-meta">
                  <span className="category">{article.category_name}</span>
                  <span className="updated">
                    Updated {new Date(article.updated_at).toLocaleDateString()}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default KnowledgeBaseHome;